import { useEffect, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, assetUrls, type Asset } from '../lib/api.js';

type Kind = 'scene_split' | 'silence_cut' | 'highlight_reel';

const KIND_INFO: Record<Kind, { label: string; blurb: string; multi: boolean }> = {
  highlight_reel: {
    label: 'Highlight reel',
    blurb:
      'Detects the shots across everything you selected, ranks them, and assembles the best into one cut. Describe what matters and the ranking follows it.',
    multi: true,
  },
  scene_split: {
    label: 'Split into shots',
    blurb:
      'Finds every cut in a long recording and exports each shot as its own clip. Useful for breaking up a continuous camera roll.',
    multi: true,
  },
  silence_cut: {
    label: 'Remove dead air',
    blurb:
      'Strips silent stretches out of a single recording. Good for debriefs and build-day timelapses with long gaps.',
    multi: false,
  },
};

export function EditPage(): ReactNode {
  const [searchParams] = useSearchParams();
  const preselected = (searchParams.get('assets') ?? '').split(',').filter(Boolean);

  const [kind, setKind] = useState<Kind>('highlight_reel');
  const [sources, setSources] = useState<Asset[]>([]);
  const [prompt, setPrompt] = useState('');
  const [targetSeconds, setTargetSeconds] = useState(60);
  const [minSceneSeconds, setMinSceneSeconds] = useState(1.5);
  const [sceneThreshold, setSceneThreshold] = useState(27);
  const [silenceThreshold, setSilenceThreshold] = useState(0.04);
  const [outputName, setOutputName] = useState('');
  const [jobs, setJobs] = useState<Awaited<ReturnType<typeof api.listEdits>>['jobs']>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (preselected.length === 0) return;
    void Promise.all(preselected.map((id) => api.getAsset(id).then((d) => d.asset)))
      .then((loaded) => setSources(loaded.filter((a) => a.kind === 'video')))
      .catch(() => setError('Could not load the selected clips'));
  }, [searchParams]);

  const refreshJobs = (): void => {
    void api
      .listEdits()
      .then((r) => setJobs(r.jobs))
      .catch(() => setJobs([]));
  };

  useEffect(() => {
    refreshJobs();
    // Renders take minutes; poll so progress advances without a manual refresh.
    const timer = setInterval(refreshJobs, 4000);
    return () => clearInterval(timer);
  }, []);

  const submit = async (): Promise<void> => {
    setError(null);
    setSubmitting(true);
    try {
      await api.createEdit({
        kind,
        sourceAssetIds: sources.map((s) => s.id),
        folderId: sources[0]?.folder_id ?? null,
        outputName: outputName.trim() || undefined,
        sceneThreshold,
        minSceneSeconds,
        silenceThreshold,
        targetSeconds,
        prompt: prompt.trim() || undefined,
      });
      refreshJobs();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not queue the job');
    } finally {
      setSubmitting(false);
    }
  };

  const info = KIND_INFO[kind];
  const tooManySources = !info.multi && sources.length > 1;

  return (
    <div className="body no-sidebar">
      <div className="main" style={{ maxWidth: 900, margin: '0 auto', width: '100%' }}>
        <h2>Auto-edit</h2>
        <p style={{ color: 'var(--text-dim)', marginTop: -8 }}>
          Rough cuts assembled automatically. Output lands in the archive as a new video you
          can download and refine in a real editor.
        </p>

        {error && <div className="notice error">{error}</div>}

        <div className="panel">
          <h3>What do you want to make?</h3>
          <div className="stack">
            {(Object.keys(KIND_INFO) as Kind[]).map((key) => (
              <label key={key} className="flex" style={{ alignItems: 'flex-start', gap: 10 }}>
                <input
                  type="radio"
                  name="kind"
                  checked={kind === key}
                  onChange={() => setKind(key)}
                  style={{ width: 'auto', marginTop: 3 }}
                />
                <span>
                  <strong>{KIND_INFO[key].label}</strong>
                  <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                    {KIND_INFO[key].blurb}
                  </div>
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="panel">
          <h3>Source clips ({sources.length})</h3>
          {sources.length === 0 ? (
            <p style={{ color: 'var(--text-dim)', fontSize: 13 }}>
              Nothing selected. Go to <Link to="/" style={{ color: 'var(--accent)' }}>Browse</Link>,
              tick the videos you want, then choose “Auto-edit these”.
            </p>
          ) : (
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}>
              {sources.map((asset) => (
                <div key={asset.id} className="card">
                  {asset.has_thumb ? (
                    <img className="thumb" src={assetUrls.thumb(asset.id)} alt="" />
                  ) : (
                    <div className="thumb placeholder">▶</div>
                  )}
                  <div className="meta">
                    <div className="name">{asset.filename}</div>
                  </div>
                  <button
                    className="btn sm"
                    style={{ position: 'absolute', top: 4, right: 4 }}
                    onClick={() => setSources((prev) => prev.filter((s) => s.id !== asset.id))}
                    aria-label="Remove"
                  >
                    x
                  </button>
                </div>
              ))}
            </div>
          )}
          {tooManySources && (
            <div className="notice error" style={{ marginTop: 10 }}>
              “{info.label}” works on one clip at a time. Remove the extras.
            </div>
          )}
        </div>

        <div className="panel">
          <h3>Settings</h3>
          <div className="stack">
            {kind === 'highlight_reel' && (
              <>
                <label>
                  <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                    What makes a good shot for this reel?
                  </div>
                  <input
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="e.g. rocket launching with a big exhaust plume"
                  />
                </label>
                <label>
                  <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                    Target length: {targetSeconds}s
                  </div>
                  <input
                    type="range"
                    min={10}
                    max={300}
                    step={5}
                    value={targetSeconds}
                    onChange={(e) => setTargetSeconds(Number(e.target.value))}
                  />
                </label>
              </>
            )}

            {kind !== 'silence_cut' && (
              <>
                <label>
                  <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                    Shortest usable shot: {minSceneSeconds}s
                  </div>
                  <input
                    type="range"
                    min={0.5}
                    max={10}
                    step={0.5}
                    value={minSceneSeconds}
                    onChange={(e) => setMinSceneSeconds(Number(e.target.value))}
                  />
                </label>
                <label>
                  <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                    Cut sensitivity: {sceneThreshold} (lower finds more cuts)
                  </div>
                  <input
                    type="range"
                    min={10}
                    max={50}
                    step={1}
                    value={sceneThreshold}
                    onChange={(e) => setSceneThreshold(Number(e.target.value))}
                  />
                </label>
              </>
            )}

            {kind === 'silence_cut' && (
              <label>
                <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                  Silence threshold: {silenceThreshold} (lower keeps quieter audio)
                </div>
                <input
                  type="range"
                  min={0.01}
                  max={0.3}
                  step={0.01}
                  value={silenceThreshold}
                  onChange={(e) => setSilenceThreshold(Number(e.target.value))}
                />
              </label>
            )}

            <label>
              <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>Output filename</div>
              <input
                value={outputName}
                onChange={(e) => setOutputName(e.target.value)}
                placeholder={`${kind}-${new Date().toISOString().slice(0, 10)}.mp4`}
              />
            </label>
          </div>

          <button
            className="btn primary"
            style={{ marginTop: 14 }}
            disabled={sources.length === 0 || tooManySources || submitting}
            onClick={() => void submit()}
          >
            {submitting ? 'Queuing…' : 'Start render'}
          </button>
        </div>

        {jobs.length > 0 && (
          <div className="panel">
            <h3>Recent renders</h3>
            <table>
              <thead>
                <tr>
                  <th>Job</th>
                  <th style={{ width: 160 }}>Progress</th>
                  <th style={{ width: 110 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job.id}>
                    <td>
                      {KIND_INFO[job.kind as Kind]?.label ?? job.kind}
                      <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                        {new Date(job.created_at).toLocaleString()}
                        {job.log && ` — ${job.log.slice(0, 120)}`}
                      </div>
                    </td>
                    <td>
                      <div className="progress">
                        <div
                          style={{
                            width: `${Math.round(job.progress * 100)}%`,
                            background:
                              job.status === 'failed' ? 'var(--danger)' : 'var(--accent)',
                          }}
                        />
                      </div>
                    </td>
                    <td>
                      {job.output_asset_id ? (
                        <Link to={`/asset/${job.output_asset_id}`} style={{ color: 'var(--accent)' }}>
                          view result
                        </Link>
                      ) : (
                        job.status
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
